import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, articleContent } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = articles.find((a) => a.id === articleId);
  const content = articleContent[articleId as keyof typeof articleContent];

  const relatedArticles = articles
    .filter((a) => a.id !== articleId && a.category === article?.category)
    .slice(0, 3);

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页吃瓜
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: content.author,
    },
    publisher: {
      '@type': 'Organization',
      name: content.source,
    },
    url: `https://www.sdfengtuan.cn/article/${article.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51吃瓜网站',
      url: 'https://www.sdfengtuan.cn',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.sdfengtuan.cn' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.sdfengtuan.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.sdfengtuan.cn/article/${article.id}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-8 md:py-12 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {content.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000
                    ? `${(article.views / 10000).toFixed(1)}万阅读`
                    : `${article.views}阅读`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-building-line"></i>
                  {content.source}
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: content.content }}
            />

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-10 p-5 md:p-6 rounded-2xl bg-background-100 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-sm text-foreground-600">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-lg text-accent-600"></i>
              </div>
              <p className="leading-relaxed">
                <strong className="text-foreground-900">声明：</strong>
                本文内容来源于网络公开信息及网友投稿，51吃瓜网站仅对内容进行整理发布，不代表本平台立场。如涉及侵权或事实不实，请联系我们进行核实处理。
              </p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 md:mb-8">
                相关<span className="text-accent-600">爆料</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer"
                  >
                    <article className="rounded-2xl overflow-hidden bg-background-50 transition-transform duration-300 group-hover:scale-[1.02]">
                      <div className="aspect-[3/2] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2 whitespace-nowrap">
                          {ra.category}
                        </span>
                        <h3 className="text-sm md:text-base font-bold text-foreground-900 leading-snug line-clamp-2">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                          <span>{ra.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {ra.views >= 10000
                              ? `${(ra.views / 10000).toFixed(1)}万`
                              : ra.views}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}